import { isBoolean } from 'lodash-es'

import {
  LayerToggleOption,
  LayerToggles,
  ResolvedLayerToggle,
  SPECIAL_TOGGLE_KEYS,
  SpecialToggleKey,
  VehicleLabelsConfig,
} from './LayerManager.types'

/**
 * Traffic and satellite are part of the panel whether or not the caller mentions them. Omitting the
 * key entirely renders them uncontrolled, ahead of everything else; naming the key instead lets the
 * caller both control them and choose where in the list they sit.
 */
const IMPLICIT_TOGGLE_KEYS = ['traffic', 'satellite'] as const

export const isSpecialToggleKey = (key: string): key is SpecialToggleKey =>
  SPECIAL_TOGGLE_KEYS.includes(key as SpecialToggleKey)

/**
 * Flattens the caller's `toggles` record into an ordered render list, resolving the four ways an
 * entry can be expressed:
 *
 * - **absent** (traffic/satellite only) — render uncontrolled, ahead of the named entries
 * - **`false`** — do not render
 * - **`true`** or `undefined` — render uncontrolled, in the caller's position
 * - **a `LayerToggleOption`** — render controlled by the caller
 *
 * A key with no purpose-built component behind it needs a full option object; a bare boolean there
 * has no state to render, so it is dropped.
 */
export const resolveLayerToggles = (toggles: LayerToggles): ResolvedLayerToggle[] => {
  const resolved: ResolvedLayerToggle[] = IMPLICIT_TOGGLE_KEYS.filter(
    (key) => !(key in toggles),
  ).map((key) => ({ key, kind: key }))

  Object.entries(toggles).forEach(([key, toggle]) => {
    if (toggle === false) return

    const option = isBoolean(toggle) || toggle === undefined ? undefined : toggle

    if (isSpecialToggleKey(key)) {
      resolved.push({ key, kind: key, option })
      return
    }

    // Generic keys have no self-managing component to fall back on.
    if (option) {
      resolved.push({ key, kind: 'generic', option })
    }
  })

  return resolved
}

/**
 * Props for a self-managing toggle. With no option object the toggle is left uncontrolled, so it
 * keeps whatever state it already holds rather than being reset to a default.
 */
export const toControlledProps = (option: LayerToggleOption | undefined) =>
  option ? { value: option.value, onValueChange: option.setValue } : {}

/** As `toControlledProps`, plus the nested options the tasks toggle reveals when it is on. */
export const toTasksProps = (option: LayerToggleOption | undefined) =>
  option
    ? {
        value: option.value,
        onValueChange: option.setValue,
        childrenOptions: option.childrenOptions,
      }
    : {}

/** Drives the "Off" reset button, which is disabled while there is nothing to reset. */
export const hasAnyVehicleLabel = (
  labels: Pick<VehicleLabelsConfig, 'showRegistration' | 'showShareReference' | 'showTags'>,
): boolean => labels.showRegistration || labels.showShareReference || labels.showTags === true
